import { ref } from 'vue'
import { defineStore } from 'pinia'

export const POPUP_PRIORITY = { deposit: 10, dailyReward: 20, season: 30 }

export const usePopupQueueStore = defineStore('popup-queue', () => {
  const activeId = ref(null)
  const paused = ref(false)

  const candidates = new Map()
  let busyChecker = () => false
  let gapMs = 500
  let runScheduled = false
  let justClosedId = null

  function pickCandidate(excludeId = null) {
    if (activeId.value || paused.value || busyChecker()) {
      return null
    }
    let best = null
    for (const candidate of candidates.values()) {
      if (candidate.id === excludeId) {
        continue
      }
      if (candidate.isReady() && (best === null || candidate.priority < best.priority)) {
        best = candidate
      }
    }
    return best
  }

  async function run() {
    const skip = justClosedId
    justClosedId = null

    const candidate = pickCandidate(skip)
    if (!candidate) {
      return
    }

    activeId.value = candidate.id
    try {
      await candidate.show()
    } finally {
      activeId.value = null
      justClosedId = candidate.id
      scheduleNext()
    }
  }

  function scheduleNext() {
    if (gapMs > 0) {
      setTimeout(run, gapMs)
    } else {
      requestRun()
    }
  }

  function requestRun() {
    if (runScheduled) {
      return
    }
    runScheduled = true
    queueMicrotask(() => {
      runScheduled = false
      run()
    })
  }

  function register(candidate) {
    candidates.set(candidate.id, candidate)
    requestRun()
  }

  function unregister(id) {
    candidates.delete(id)
  }

  function setPaused(value) {
    paused.value = value
    if (!value) {
      requestRun()
    }
  }

  function setBusyChecker(fn) {
    busyChecker = typeof fn === 'function' ? fn : () => false
  }

  function configure({ gapMs: gap } = {}) {
    if (gap != null) {
      gapMs = gap
    }
  }

  return {
    activeId,
    paused,
    register,
    unregister,
    requestRun,
    setPaused,
    setBusyChecker,
    configure,
    pickCandidate,
    run,
  }
})
